import React, {useEffect, useRef, useState} from 'react'
import {useMediaQuery}                      from '@mui/material'

/*
 * Dependency-free confetti burst (076).
 *
 * A canvas-confetti package would have been the obvious choice, but CLAUDE.md forbids new
 * dependencies without explicit instruction, so this is a small self-contained particle
 * system instead. It is deliberately minimal: one burst, no continuous emitter, no options
 * beyond what the score screen needs.
 *
 * Usage: mount it and flip `fire` to true. It runs once per rising edge of `fire`.
 *
 *   <Confetti fire={passed} colors={[...]}/>
 *
 * The canvas is fixed and pointer-events:none, so it never intercepts clicks, and it renders
 * null until the first burst so it costs nothing on screens that never trigger it.
 */

const GRAVITY = 0.16
const DRAG = 0.992
const SPREAD = 1.15  // radians either side of straight up

const Confetti = ({fire, colors, count = 150, duration = 2600}) => {
	const canvasRef = useRef(null)
	const rafRef = useRef(null)
	const [active, setActive] = useState(false)

	// honour the OS setting: the badge and score still appear, only the motion is skipped
	const reduceMotion = useMediaQuery('(prefers-reduced-motion: reduce)')

	useEffect(() => {
		if (!fire || reduceMotion) return
		setActive(true)
	}, [fire, reduceMotion])

	useEffect(() => {
		if (!active) return

		const canvas = canvasRef.current
		if (!canvas) return

		const ctx = canvas.getContext('2d')
		const dpr = window.devicePixelRatio || 1
		const w = window.innerWidth
		const h = window.innerHeight

		// size the backing store to the device pixel ratio so the pieces aren't blurry
		canvas.width = w * dpr
		canvas.height = h * dpr
		canvas.style.width = `${w}px`
		canvas.style.height = `${h}px`
		ctx.scale(dpr, dpr)

		// burst from behind the score badge rather than the centre of the viewport
		const originX = w / 2
		const originY = h * 0.28

		const palette = (colors && colors.length) ? colors : ['#6366F1', '#34D399', '#F59E0B']

		const particles = Array.from({length: count}, () => {
			// upward-biased radial spread
			const angle = -Math.PI / 2 + (Math.random() * 2 - 1) * SPREAD
			const speed = 5 + Math.random() * 8
			return {
				x: originX + (Math.random() * 2 - 1) * 40,
				y: originY + (Math.random() * 2 - 1) * 20,
				vx: Math.cos(angle) * speed,
				vy: Math.sin(angle) * speed,
				w: 5 + Math.random() * 6,
				h: 8 + Math.random() * 8,
				rot: Math.random() * Math.PI,
				vr: (Math.random() * 2 - 1) * 0.22,
				color: palette[Math.floor(Math.random() * palette.length)],
			}
		})

		const start = performance.now()

		const frame = (now) => {
			const elapsed = now - start
			const progress = Math.min(elapsed / duration, 1)

			ctx.clearRect(0, 0, w, h)

			// fade the whole burst out over the last third so pieces don't just vanish
			ctx.globalAlpha = progress < 0.66 ? 1 : 1 - (progress - 0.66) / 0.34

			for (const p of particles) {
				p.vy += GRAVITY
				p.vx *= DRAG
				p.vy *= DRAG
				p.x += p.vx
				p.y += p.vy
				p.rot += p.vr

				ctx.save()
				ctx.translate(p.x, p.y)
				ctx.rotate(p.rot)
				ctx.fillStyle = p.color
				ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h)
				ctx.restore()
			}

			if (progress < 1) {
				rafRef.current = requestAnimationFrame(frame)
			}
			else {
				ctx.clearRect(0, 0, w, h)
				setActive(false)
			}
		}

		rafRef.current = requestAnimationFrame(frame)

		// leaving the screen mid-burst must not leave the loop running
		return () => {
			if (rafRef.current) cancelAnimationFrame(rafRef.current)
		}
	}, [active, colors, count, duration])

	if (!active) return null

	return (
		<canvas
			ref={canvasRef}
			aria-hidden="true"
			style={{
				position: 'fixed',
				top: 0,
				left: 0,
				width: '100%',
				height: '100%',
				pointerEvents: 'none',
				zIndex: 1400,
			}}
		/>
	)
}

export default Confetti
